// src/pages/ReceivePage.tsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Input, Button, Form, message, Table, Tag, Popconfirm, Modal, InputNumber, Space, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, PrinterOutlined } from '@ant-design/icons';
import { apiDeleteInbound, apiUpdateInboundLine, apiCreateInbound } from '../services/inboundAPI';
import { fetchWithAuth } from '../services/auth';
import { dbAPI, STORE_NAMES } from '../utils/db';
import type { StoreName } from '../utils/db';
import ProductHeader from '../components/ProductHeader';
import InboundModal from '../components/InboundModal';
import LabelPrintModal from '../components/LabelPrintModal';
import { useIsMobile } from '../hooks/useIsMobile';
import { apiGetInbounds } from '../services/inboundAPI';
import { apiSearchBySku } from '../services/productAPI';

const CATEGORY_TO_STORE: Record<string, StoreName> = {
  spark_plugs:               STORE_NAMES.sparkPlugs,
  ignition_coils:            STORE_NAMES.coils,
  crankshaft_sensors:        STORE_NAMES.sensors,
  camshaft_sensors:          STORE_NAMES.sensors,
  airbag_cables:             STORE_NAMES.steeringClocks,
  ignition_coil_mouthpieces: STORE_NAMES.wires,
};

const STORE_LABELS: Record<string, string> = {
  [STORE_NAMES.sparkPlugs]:     'Свечи зажигания',
  [STORE_NAMES.coils]:          'Катушки зажигания',
  [STORE_NAMES.wires]:          'Наконечники и провода',
  [STORE_NAMES.sensors]:        'Датчики',
  [STORE_NAMES.steeringClocks]: 'Рулевые шлейфы',
};

type FoundProduct = {
  id: string;
  brand: string;
  partNumber: string;
  model: string;
  sku: string;
  storeName: StoreName;
};

export type InboundRecord = {
  id: string;
  createdAt: string;
  sku: string;
  brand: string;
  partNumber: string;
  model: string;
  category: string;
  qty: number;
  unitCost: number;
  totalCost: number;
  supplier: string;
};

const isOrphanedHeader = (id: string) => id.split('-').length <= 5;

const buildColumns = (
  onDelete: (id: string) => void,
  onEdit: (r: InboundRecord) => void,
) => [
  {
    title: 'Дата', dataIndex: 'createdAt', key: 'createdAt', width: 140,
    render: (v: string) => new Date(v).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }),
  },
  { title: 'SKU',          dataIndex: 'sku',        key: 'sku',        width: 80  },
  { title: 'Бренд',        dataIndex: 'brand',      key: 'brand',      width: 100 },
  { title: 'Номер детали', dataIndex: 'partNumber', key: 'partNumber', width: 130 },
  { title: 'Кол-во',       dataIndex: 'qty',        key: 'qty',        width: 80  },
  {
    title: 'Цена закупки', dataIndex: 'unitCost', key: 'unitCost', width: 120,
    render: (v: number) => v ? `${v.toLocaleString('ru-RU')} ֏` : '—',
  },
  {
    title: 'Итого', dataIndex: 'totalCost', key: 'totalCost', width: 110,
    render: (v: number) => v ? <b>{v.toLocaleString('ru-RU')} ֏</b> : '—',
  },
  { title: 'Поставщик', dataIndex: 'supplier', key: 'supplier', width: 130 },
  {
    title: 'Категория', dataIndex: 'category', key: 'category', width: 150,
    render: (v: string) => STORE_LABELS[v] ?? v,
  },
  {
    title: 'Удалить', key: 'actions', width: 100, align: 'center' as const,
    render: (_: any, r: InboundRecord) => (
      <Space size={12}>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => onEdit(r)}
          disabled={isOrphanedHeader(r.id)}
        />
        <Popconfirm
          title="Удалить запись?"
          okText="Да" cancelText="Нет"
          onConfirm={() => onDelete(r.id)}
        >
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ),
  },
];

const ReceivePage: React.FC = () => {
  const isMobile = useIsMobile();
  const [skuInput, setSkuInput]     = useState('');
  const [found, setFound]           = useState<FoundProduct | null>(null);
  const [notFound, setNotFound]     = useState(false);
  const [searching, setSearching]   = useState(false);
  const [records, setRecords]       = useState<InboundRecord[]>([]);
  const [modalOpen, setModalOpen]   = useState(false);
  const [labelOpen, setLabelOpen]   = useState(false);
  const [editRecord, setEditRecord] = useState<InboundRecord | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const inputRef = useRef<any>(null);

  const load = useCallback(async () => {
    try {
      // Fetch from MySQL and flatten lines into individual records
      const remote = await apiGetInbounds();
      const flat: InboundRecord[] = [];
      for (const ib of remote) {
        const lines = ib.lines ?? [];
        if (lines.length > 0) {
          for (const l of lines) {
            flat.push({
              id:         `${ib.id}-${l.id ?? flat.length}`,
              createdAt:  ib.created_at ?? ib.createdAt ?? new Date().toISOString(),
              sku:        l.sku         ?? '',
              brand:      l.brand       ?? '',
              partNumber: l.part_number ?? '',
              model:      l.model       ?? '',
              category:   l.category    ?? '',
              qty:        Number(l.qty ?? 0),
              unitCost:   Number(l.unit_cost ?? 0),
              totalCost:  Number(l.total_cost ?? 0),
              supplier:   ib.supplier   ?? '',
            } as InboundRecord);
          }
        } else {
          // Orphaned inbound header (all lines deleted) — show so user can remove it
          flat.push({
            id:         ib.id,
            createdAt:  ib.created_at ?? ib.createdAt ?? new Date().toISOString(),
            sku:        '',
            brand:      '',
            partNumber: '',
            model:      '',
            category:   '',
            qty:        0,
            unitCost:   0,
            totalCost:  0,
            supplier:   ib.supplier ?? '',
          } as InboundRecord);
        }
      }
      flat.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setRecords(flat.slice(0, 200));
    } catch {
      // Fallback to IndexedDB
      const all = await dbAPI.getAll<InboundRecord>(STORE_NAMES.inbounds);
      const real = all
        .filter((r: any) => typeof r.qty === 'number')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 100);
      setRecords(real);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      const apiBase = process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api';
      const parts = id.split('-');

      if (parts.length <= 5) {
        // Pure UUID — orphaned inbound header with no lines
        await apiDeleteInbound(id);
      } else {
        // Composed id: UUID (5 groups) + lineId (1 numeric group) = 6 parts
        const inboundUUID = parts.slice(0, 5).join('-');
        const lineId = parts[5];
        const siblingCount = records.filter(r => r.id.startsWith(inboundUUID + '-')).length;

        if (siblingCount <= 1) {
          // Last (or only) line — delete the whole inbound; CASCADE removes the line too
          await apiDeleteInbound(inboundUUID);
        } else {
          const res = await fetchWithAuth(`${apiBase}/inbound/line/${lineId}`, { method: 'DELETE' });
          if (!res.ok) throw new Error();
        }
      }

      message.success('Запись удалена');
      load();
    } catch {
      message.error('Ошибка удаления');
    }
  }, [records, load]);

  const handleEdit = useCallback((r: InboundRecord) => {
    setEditRecord(r);
    editForm.setFieldsValue({
      brand:      r.brand,
      partNumber: r.partNumber,
      qty:        r.qty,
      unitCost:   r.unitCost,
    });
  }, [editForm]);

  const handleEditSave = useCallback(async () => {
    if (!editRecord) return;
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      const parts = editRecord.id.split('-');
      const lineId = parts[5];
      await apiUpdateInboundLine(lineId, {
        qty:        values.qty,
        unitCost:   values.unitCost,
        brand:      values.brand,
        partNumber: values.partNumber,
      });
      message.success('Изменения сохранены');
      setEditRecord(null);
      editForm.resetFields();
      load();
    } catch {
      message.error('Ошибка сохранения');
    } finally {
      setEditSaving(false);
    }
  }, [editRecord, editForm, load]);

  const handleSearch = useCallback(async (raw: string) => {
    const q = raw.trim();
    if (!q) return;
    setSearching(true);
    setFound(null);
    setNotFound(false);

    try {
      const results = await apiSearchBySku(q);
      if (results.length > 0) {
        const p = results[0];
        const storeName = CATEGORY_TO_STORE[p.category_name] ?? STORE_NAMES.sparkPlugs;
        setFound({
          id:         String(p.id ?? p.product_id ?? ''),
          brand:      p.brand      ?? '',
          partNumber: p.partNumber ?? p.detail_number ?? '',
          model:      p.model      ?? '',
          sku:        p.sku        ?? q,
          storeName,
        });
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSubmit = useCallback(
    async (values: { qty: string; unitCost: string; supplier?: string; quality?: 'original' | 'copy' }) => {
      if (!found) return;
      const qty      = Number(values.qty);
      const unitCost = Number(values.unitCost);
      try {
        // Пишем в MySQL (как InboundModal): запись сохраняется, склад растёт,
        // строка получает реальный id → работают кнопки Изменить/Удалить.
        await apiCreateInbound({
          id:       crypto.randomUUID(),
          supplier: values.supplier ?? '',
          status:   'ready',
          lines: [{
            sku:        found.sku,
            brand:      found.brand,
            partNumber: found.partNumber,
            model:      found.model,
            category:   found.storeName,
            qty,
            unitCost,
            totalCost:  qty * unitCost,
            quality:    values.quality ?? 'original',
          }],
        });
        message.success(`Записано: ${found.brand} ${found.partNumber} × ${qty} шт.`);
        setFound(null);
        setNotFound(false);
        setSkuInput('');
        form.resetFields();
        await load();
        setTimeout(() => inputRef.current?.focus(), 50);
      } catch (e: any) {
        message.error(e?.message || 'Ошибка сохранения поступления');
      }
    },
    [found, form, load],
  );

  return (
    <div style={{ padding: '20px' }}>
      <ProductHeader
        title="Принять товар"
        addButton={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            Добавить
          </Button>
        }
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Input
          ref={inputRef}
          autoFocus
          placeholder="Сканируйте штрихкод или введите SKU"
          value={skuInput}
          onChange={(e) => setSkuInput(e.target.value)}
          onPressEnter={() => handleSearch(skuInput)}
          style={isMobile ? { flex: 1, minWidth: 0 } : { width: 340 }}
          allowClear
          onClear={() => { setFound(null); setNotFound(false); }}
        />
        <Button
          type="primary"
          loading={searching}
          onClick={() => handleSearch(skuInput)}
        >
          Найти
        </Button>
      </div>

      {notFound && (
        <div style={{ marginBottom: 16 }}>
          <Tag color="error">
            Товар с SKU «{skuInput}» не найден в каталоге — сначала добавьте его через раздел Товары
          </Tag>
        </div>
      )}

      {found && (
        <div
          style={{
            marginBottom: 24,
            padding: '14px 16px',
            background: '#141414',
            borderRadius: 8,
            border: '1px solid #303030',
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <strong style={{ fontSize: 15 }}>
              {found.brand} {found.partNumber}
            </strong>
            {found.model && (
              <span style={{ color: '#8c8c8c', marginLeft: 6 }}>
                / {found.model}
              </span>
            )}
            <Tag color="blue" style={{ marginLeft: 10 }}>
              {STORE_LABELS[found.storeName] ?? found.storeName}
            </Tag>
            <Tag style={{ marginLeft: 4 }}>SKU: {found.sku}</Tag>
            <Button
              size="small"
              icon={<PrinterOutlined />}
              style={{ marginLeft: 8 }}
              onClick={() => setLabelOpen(true)}
            >
              Печать этикетки
            </Button>
          </div>

          <Form form={form} layout="inline" onFinish={handleSubmit}>
            <Form.Item
              name="qty"
              rules={[{ required: true, message: 'Укажите кол-во' }]}
            >
              <Input
                type="number"
                placeholder="Кол-во"
                min={1}
                style={{ width: 90 }}
                autoFocus
              />
            </Form.Item>
            <Form.Item
              name="unitCost"
              rules={[{ required: true, message: 'Укажите цену' }]}
            >
              <Input
                type="number"
                placeholder="Цена закупки (֏)"
                min={0}
                style={{ width: 150 }}
              />
            </Form.Item>
            <Form.Item name="quality" initialValue="original">
              <Select
                style={{ width: 120 }}
                options={[
                  { value: 'original', label: 'Оригинал' },
                  { value: 'copy',     label: 'Копия' },
                ]}
              />
            </Form.Item>
            <Form.Item name="supplier">
              <Input placeholder="Поставщик" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Записать поступление
              </Button>
            </Form.Item>
          </Form>
        </div>
      )}

      <h3 style={{ marginTop: 32, marginBottom: 12 }}>История поступлений</h3>
      <Table
        dataSource={records}
        columns={buildColumns(handleDelete, handleEdit)}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 20, position: ['bottomCenter'], showSizeChanger: false }}
        scroll={{ x: 950 }}
        locale={{ emptyText: 'Поступлений пока нет' }}
      />

      <InboundModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => { setModalOpen(false); load(); }}
      />

      <LabelPrintModal
        open={labelOpen}
        onClose={() => setLabelOpen(false)}
        product={found ? { sku: found.sku, brand: found.brand, partNumber: found.partNumber } : null}
      />

      <Modal
        open={!!editRecord}
        onCancel={() => { setEditRecord(null); editForm.resetFields(); }}
        onOk={handleEditSave}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={editSaving}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="brand" label="Бренд">
            <Input />
          </Form.Item>
          <Form.Item name="partNumber" label="Номер детали">
            <Input />
          </Form.Item>
          <Form.Item name="qty" label="Кол-во" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="unitCost" label="Цена закупки (֏)" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReceivePage;
